import React from 'react';
import { EP_PROJECT_DOCUMENTS } from '../../../functions/src/common/epProjectWorkflow';
import { epProjectDocumentBlocks, epProjectStatusTone, type EpProjectTextRun } from '../../../functions/src/common/epProjectDocument';
import { formatLocalDate, formatLocalDateTime } from '../../utils/localDateTime';
import './epProjectReport.css';

export type EpProjectReportProps = {
    reference: string;
    documents: Record<string, string>;
    classification?: string;
    name?: string;
    threatLevel?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    signature?: string;
    signedAt?: string | null;
    draft?: boolean;
    aarDraft?: boolean;
};

const Inline = ({ runs }: { runs: EpProjectTextRun[] }) => <>{runs.map((run, index) => {
    let text: React.ReactNode = run.text;
    if (run.italic) text = <em>{text}</em>;
    if (run.bold) text = <strong>{text}</strong>;
    return run.link ? <a key={index} href={run.link} rel="noopener noreferrer">{text}</a> : <React.Fragment key={index}>{text}</React.Fragment>;
})}</>;

export function EpProjectDocumentContent({ content }: { content: string }) {
    const blocks = epProjectDocumentBlocks(content);
    const elements: React.ReactNode[] = [];
    for (let index = 0; index < blocks.length; index++) {
        const block = blocks[index];
        const key = index;
        if (block.kind === 'heading') {
            const Heading = `h${Math.min(block.level + 1, 6)}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
            elements.push(<Heading key={key}><Inline runs={block.runs} /></Heading>);
        } else if (block.kind === 'table')
            elements.push(<div key={key} className="ep-project-table-wrap"><table><thead><tr>{block.headers.map((runs, column) => <th key={column} scope="col"><Inline runs={runs} /></th>)}</tr></thead><tbody>{block.rows.map((row, at) => <tr key={at}>{row.map((runs, column) => {
                const tone = epProjectStatusTone(runs.map(run => run.text).join(''));
                return <td key={column}>{tone === 'neutral' ? <Inline runs={runs} /> : <span className={`ep-project-tag ep-project-tag-${tone}`}><Inline runs={runs} /></span>}</td>;
            })}</tr>)}</tbody></table></div>);
        else if (block.kind === 'rule') elements.push(<hr key={key} />);
        else if (block.marker) {
            const ordered = block.marker !== '-';
            const start = Number.parseInt(block.marker, 10);
            const items = [block];
            while (index + 1 < blocks.length) {
                const next = blocks[index + 1];
                if (next.kind !== 'paragraph' || !next.marker || next.indent !== block.indent ||
                    (next.marker !== '-') !== ordered || ordered && Number.parseInt(next.marker, 10) !== start + items.length)
                    break;
                items.push(next);
                index++;
            }
            const children = items.map((item, at) => <li key={at}><Inline runs={item.runs} /></li>);
            elements.push(<div key={key} className="ep-project-list" style={{ paddingLeft: Math.max(0, block.indent - 1) * 20 }}>
                {ordered ? <ol start={start}>{children}</ol> : <ul>{children}</ul>}
            </div>);
        } else if (block.quote)
            elements.push(<blockquote key={key}><Inline runs={block.runs} /></blockquote>);
        else elements.push(<p key={key}><Inline runs={block.runs} /></p>);
    }
    return <div className="ep-project-document">{elements}</div>;
}

export default function EpProjectReportDisplay({ reference, documents, classification = 'Confidential', name, threatLevel,
    startDate, endDate, signature, signedAt, draft = false, aarDraft = false }: EpProjectReportProps) {
    const aarOnly = Boolean(documents.aar) && EP_PROJECT_DOCUMENTS.every(document => document.key === 'aar' || !documents[document.key]);
    return <article className="ep-project-report">
        <header className="ep-project-header"><div><p className="ep-project-brand">INTELOPTICS INSIGHTS</p><h1>{aarOnly ? 'After Action Review' : 'EP Project Plan'}</h1><p className="ep-project-reference">{reference}{name && ` · ${name}`}</p></div><div className="ep-project-labels"><span className="ep-project-tag">{classification}</span>{threatLevel && <span className={`ep-project-tag ep-project-tag-${epProjectStatusTone(threatLevel)}`}>Threat: {threatLevel}</span>}<span className="ep-project-status">{draft ? 'Consultant draft' : aarOnly ? 'Approved After Action Review' : 'Approved operations package'}</span></div></header>
        {(startDate || endDate) && <dl className="ep-project-profile"><div><dt>Project start</dt><dd>{formatLocalDate(startDate, 'Not recorded')}</dd></div><div><dt>Project end</dt><dd>{formatLocalDate(endDate, 'Not recorded')}</dd></div></dl>}
        {EP_PROJECT_DOCUMENTS.map((document, index) => documents[document.key] && <section className="ep-project-section" key={document.key}>
            <header className="ep-project-section-heading"><span>{String(index + 1).padStart(2, '0')}</span><h2>{document.title}</h2></header>
            {document.key === 'aar' && aarDraft && <p className="ep-project-draft-note">Consultant draft. Not yet released to the client.</p>}
            <EpProjectDocumentContent content={documents[document.key]} />
        </section>)}
        {signature && <aside className="ep-project-signature"><h2>{aarOnly ? 'After Action Review sign-off' : 'Operations package sign-off'}</h2><p><strong>{signature}</strong></p>{signedAt && <p>Signed: {formatLocalDateTime(signedAt, 'Not recorded')}</p>}</aside>}
    </article>;
}
